import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  LinearProgress,
  Menu,
  MenuList,
  MenuItem as MenuItemComponent,
  ListItemIcon,
  ListItemText,
  Grid
} from '@mui/material'
import {
  Visibility,
  Edit,
  Delete,
  Verified,
  Pending,
  Error as ErrorIcon,
  MoreVert,
  Download,
  History,
  ContentCopy
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { format } from 'date-fns'
import medicalCodingService from '../../../services/medicalCodingService'

const CodingList = ({ onEdit, onView }) => {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  
  const [loading, setLoading] = useState(false)
  const [codingList, setCodingList] = useState([])
  const [filteredList, setFilteredList] = useState([])
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [totalCount, setTotalCount] = useState(0)
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('')
  const [verificationFilter, setVerificationFilter] = useState('ALL')
  const [dateFilter, setDateFilter] = useState('ALL')
  
  // Menu
  const [anchorEl, setAnchorEl] = useState(null)
  const [selectedCoding, setSelectedCoding] = useState(null)

  useEffect(() => {
    loadCodingList()
  }, [page, rowsPerPage, verificationFilter, dateFilter])

  useEffect(() => {
    filterCodingList()
  }, [searchQuery, codingList])

  const loadCodingList = async () => {
    setLoading(true)
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        verificationStatus: verificationFilter !== 'ALL' ? verificationFilter : undefined,
        dateRange: dateFilter !== 'ALL' ? dateFilter : undefined
      }

      const response = await medicalCodingService.getAllCoding(params)
      
      if (response.data.success) {
        const codingData = response.data.data
        // Handle paginated response structure
        const codingsList = codingData?.docs || codingData || []
        setCodingList(Array.isArray(codingsList) ? codingsList : [])
        setTotalCount(codingData?.total || response.data.total || codingsList.length)
      }
    } catch (error) {
      enqueueSnackbar('Failed to load coding list', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const filterCodingList = () => {
    if (!searchQuery) {
      setFilteredList(codingList)
      return
    }

    const filtered = codingList.filter(coding => 
      coding.mrdFile?.fileNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      coding.mrdFile?.patientName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      coding.primaryDiagnosis?.icd10Code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      coding.primaryDiagnosis?.icd10Description?.toLowerCase().includes(searchQuery.toLowerCase())
    )
    
    setFilteredList(filtered)
  }

  const handleVerifyCoding = async (codingId, status, remarks = '') => {
    try {
      await medicalCodingService.verifyCoding(codingId, { status, remarks })
      enqueueSnackbar(`Coding ${status.toLowerCase()}`, { variant: 'success' })
      loadCodingList()
    } catch (error) {
      enqueueSnackbar('Failed to verify coding', { variant: 'error' })
    }
  }

  const handleDeleteCoding = async (codingId) => {
    if (window.confirm('Are you sure you want to delete this coding record?')) {
      try {
        await medicalCodingService.deleteCoding(codingId)
        enqueueSnackbar('Coding deleted successfully', { variant: 'success' })
        loadCodingList()
      } catch (error) {
        enqueueSnackbar('Failed to delete coding', { variant: 'error' })
      }
    }
  }

  const handleMenuOpen = (event, coding) => {
    setAnchorEl(event.currentTarget)
    setSelectedCoding(coding)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setSelectedCoding(null)
  }

  // Temporarily commented out - Duplicate and Export functionality
  /*
  const handleDuplicateCoding = async (coding) => {
    try {
      // Create a copy of the coding data without the ID and file reference
      const duplicateData = {
        primaryDiagnosis: coding.primaryDiagnosis,
        secondaryDiagnoses: coding.secondaryDiagnoses,
        procedures: coding.procedures,
        drgGroup: coding.drgGroup
      }
      
      // Navigate to new coding form with pre-filled data
      navigate('/coding/new', { state: { duplicateData } })
      enqueueSnackbar('Coding duplicated. Please select a new MRD file.', { variant: 'info' })
    } catch (error) {
      enqueueSnackbar('Failed to duplicate coding', { variant: 'error' })
    }
  }

  const handleExportCoding = (coding) => {
    try {
      // Create export data
      const exportData = {
        mrdFile: {
          fileNumber: coding.mrdFile?.fileNumber,
          patientName: coding.mrdFile?.patientName
        },
        primaryDiagnosis: coding.primaryDiagnosis,
        secondaryDiagnoses: coding.secondaryDiagnoses,
        procedures: coding.procedures,
        drgGroup: coding.drgGroup,
        verificationStatus: coding.verificationStatus,
        codedBy: coding.codedBy?.name,
        codedAt: coding.codedAt,
        verifiedBy: coding.verifiedBy?.name,
        verifiedAt: coding.verifiedAt
      }

      // Convert to JSON and download
      const dataStr = JSON.stringify(exportData, null, 2)
      const dataBlob = new Blob([dataStr], { type: 'application/json' })
      const url = URL.createObjectURL(dataBlob)
      
      const link = document.createElement('a')
      link.href = url
      link.download = `coding-${coding.mrdFile?.fileNumber || coding._id}.json`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      
      enqueueSnackbar('Coding exported successfully', { variant: 'success' })
    } catch (error) {
      enqueueSnackbar('Failed to export coding', { variant: 'error' })
    }
  }
  */

  const getVerificationChip = (status) => {
    const config = {
      VERIFIED: { color: 'success', icon: <Verified fontSize="small" /> },
      PENDING: { color: 'warning', icon: <Pending fontSize="small" /> },
      REJECTED: { color: 'error', icon: <ErrorIcon fontSize="small" /> },
      QUERY_RAISED: { color: 'info', icon: <ErrorIcon fontSize="small" /> }
    }
    
    const { color, icon } = config[status] || { color: 'default', icon: null }
    
    return (
      <Chip
        label={status?.replace('_', ' ')}
        color={color}
        size="small"
        icon={icon}
      />
    )
  }

  const displayList = searchQuery ? filteredList : codingList

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" fontWeight={600}>
          Medical Coding Records
        </Typography>
        {/* <Button
          variant="contained"
          onClick={() => navigate('/hospital/mrd/coding/new')}
        >
          New Coding
        </Button> */}
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              placeholder="Search by IPD, patient name, or ICD code..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </Grid>
          
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Verification Status</InputLabel>
              <Select
                value={verificationFilter}
                label="Verification Status"
                onChange={(e) => setVerificationFilter(e.target.value)}
              >
                <MenuItem value="ALL">All Status</MenuItem>
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="VERIFIED">Verified</MenuItem>
                <MenuItem value="REJECTED">Rejected</MenuItem>
                <MenuItem value="QUERY_RAISED">Query Raised</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Date Range</InputLabel>
              <Select
                value={dateFilter}
                label="Date Range"
                onChange={(e) => setDateFilter(e.target.value)}
              >
                <MenuItem value="ALL">All Time</MenuItem>
                <MenuItem value="TODAY">Today</MenuItem>
                <MenuItem value="WEEK">This Week</MenuItem>
                <MenuItem value="MONTH">This Month</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              onClick={loadCodingList}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Loading */}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>IPD Details</TableCell>
              <TableCell>Primary Diagnosis</TableCell>
              <TableCell>Secondary</TableCell>
              <TableCell>Procedures</TableCell>
              <TableCell>DRG</TableCell>
              <TableCell>Verification</TableCell>
              <TableCell>Coded Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayList.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Alert severity="info">
                    {loading ? 'Loading...' : 'No coding records found'}
                  </Alert>
                </TableCell>
              </TableRow>
            ) : (
              displayList.map((coding) => (
                <TableRow key={coding._id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {coding.mrdFile?.fileNumber}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {coding.mrdFile?.patientName}
                      </Typography>
                    </Box>
                  </TableCell>
                  
                  <TableCell>
                    <Box>
                      <Chip
                        label={coding.primaryDiagnosis?.icd10Code}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                      <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                        {coding.primaryDiagnosis?.icd10Description?.slice(0, 40)}...
                      </Typography>
                    </Box>
                  </TableCell>
                  
                  <TableCell align="center">
                    <Chip
                      label={coding.secondaryDiagnoses?.length || 0}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  
                  <TableCell align="center">
                    <Chip
                      label={coding.procedures?.length || 0}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  
                  <TableCell>
                    {coding.drgGroup?.code ? (
                      <Tooltip title={coding.drgGroup.description}>
                        <Chip
                          label={coding.drgGroup.code}
                          size="small"
                          color="secondary"
                          variant="outlined"
                        />
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Not calculated
                      </Typography>
                    )}
                  </TableCell>
                  
                  <TableCell>
                    {getVerificationChip(coding.verificationStatus)}
                  </TableCell>
                  
                  <TableCell>
                    <Typography variant="body2">
                      {coding.codedAt ? format(new Date(coding.codedAt), 'MMM dd, yyyy') : '-'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {coding.codedBy?.name}
                    </Typography>
                  </TableCell>
                  
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => onView ? onView(coding) : navigate(`/hospital/mrd/coding/${coding.mrdFile._id}`)}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => onEdit ? onEdit(coding) : navigate(`/hospital/mrd/coding/${coding.mrdFile._id}/edit`)}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title="Verification">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => navigate(`/hospital/mrd/coding/${coding._id}/verify`)}
                        >
                          <Verified fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, coding)}
                      >
                        <MoreVert fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10))
            setPage(0)
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </TableContainer>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItemComponent onClick={() => {
          navigate(`/hospital/mrd/coding/${selectedCoding?.mrdFile?._id}/versions`)
          handleMenuClose()
        }}>
          <ListItemIcon>
            <History fontSize="small" />
          </ListItemIcon>
          <ListItemText>Version History</ListItemText>
        </MenuItemComponent>
        
        <MenuItemComponent onClick={() => {
          navigate(`/hospital/mrd/coding/${selectedCoding?._id}/audit`)
          handleMenuClose()
        }}>
          <ListItemIcon>
            <Visibility fontSize="small" />
          </ListItemIcon>
          <ListItemText>Audit Trail</ListItemText>
        </MenuItemComponent>
        
        {/* Temporarily commented out - Duplicate and Export functionality */}
        {/*
        <MenuItemComponent onClick={() => {
          handleDuplicateCoding(selectedCoding)
          handleMenuClose()
        }}>
          <ListItemIcon>
            <ContentCopy fontSize="small" />
          </ListItemIcon>
          <ListItemText>Duplicate</ListItemText>
        </MenuItemComponent>
        
        <MenuItemComponent onClick={() => {
          handleExportCoding(selectedCoding)
          handleMenuClose()
        }}>
          <ListItemIcon>
            <Download fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export</ListItemText>
        </MenuItemComponent>
        */}
        
        <MenuItemComponent 
          onClick={() => {
            handleDeleteCoding(selectedCoding?._id)
            handleMenuClose()
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <Delete fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItemComponent>
      </Menu>
    </Box>
  )
}

export default CodingList